import React from 'react';

import type {
  HandlerHttpCallback,
  HandlerKafkaCallback,
  HandlerRecord,
} from '@vlabs/api/artemy/model';
import { POLICIES } from '@vlabs/api-bindings/src/luna-client/handlers/constants';
import { useTranslation } from 'react-i18next';

import {
  ViewCard, ViewRow, ViewRowCheckbox, ViewRowTitle,
} from '../components';
import { useHandlerViewPageTranslation } from '../i18n';
import { CALLBACK_TYPE_KAFKA, isEnabled, isLunaWsCallback } from '../viewModel';
import { FilterRows } from './FilterRows';
import { optionLabel } from './options';

interface CallbacksSectionProps {
  handler: HandlerRecord;
}

interface CallbackCardProps {
  callback: HandlerHttpCallback | HandlerKafkaCallback;
  index: number;
}

function CallbackCard({ callback, index }: CallbackCardProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { t: tView } = useHandlerViewPageTranslation();

  const isKafka = callback.type === CALLBACK_TYPE_KAFKA;

  const typeValue = isKafka
    ? tView('callbacks.kafka.type')
    : t('constants:http');

  const row = (key: string, label: React.ReactNode, content: React.ReactNode): React.JSX.Element => (
    <ViewRow
      key={key}
      label={label}
    >
      {content}
    </ViewRow>
  );

  const rows: React.ReactNode[] = [];

  if (isKafka) {
    const kafka = callback;
    const servers = Array.isArray(kafka.servers) ? kafka.servers.join(', ') : kafka.servers;

    rows.push(row('servers', tView('callbacks.kafka.servers'), servers));
    rows.push(row('topic', tView('callbacks.kafka.topic'), kafka.topic));
    if (kafka.protocol) {
      rows.push(row('protocol', tView('callbacks.kafka.protocol'), kafka.protocol));
    }
    if (kafka.username) {
      rows.push(row('username', tView('callbacks.kafka.username'), kafka.username));
    }
    if (kafka.password) {
      rows.push(row('password', tView('callbacks.kafka.password'), kafka.password));
    }
  } else {
    const http = callback;
    const authorizationLabel = http.authorization?.type
      ? optionLabel(POLICIES.CALLBACK.authorization.type, http.authorization.type)
      : undefined;
    const contentTypeLabel = http.params?.content_type
      ? optionLabel(POLICIES.CALLBACK.params.content_type, http.params.content_type)
      : undefined;

    rows.push(row(
      'url',
      t('handlers:policies.storage_policy.callbacks.callback.url.label'),
      http.url,
    ));
    if (authorizationLabel) {
      rows.push(row(
        'auth-type',
        t('handlers:policies.storage_policy.callbacks.callback.authorization.type'),
        authorizationLabel,
      ));
    }
    if (http.authorization?.login) {
      rows.push(row(
        'login',
        t('handlers:policies.storage_policy.callbacks.callback.authorization.login.label'),
        http.authorization.login,
      ));
    }
    if (http.authorization?.password) {
      rows.push(row(
        'password',
        t('handlers:policies.storage_policy.callbacks.callback.authorization.password.label'),
        http.authorization.password,
      ));
    }
    if (http.params?.timeout !== undefined && http.params.timeout !== null) {
      rows.push(row(
        'timeout',
        t('handlers:policies.storage_policy.callbacks.callback.params.timeout.label'),
        String(http.params.timeout),
      ));
    }
    if (contentTypeLabel) {
      rows.push(row(
        'content-type',
        t('handlers:policies.storage_policy.callbacks.callback.params.content_type'),
        contentTypeLabel,
      ));
    }
    if (http.params?.headers) {
      const headers = Object.entries(http.params.headers)
        .map(([name, value]) => `${name}: ${String(value)}`)
        .join(', ');

      if (headers) {
        rows.push(row(
          'headers',
          t('handlers:policies.storage_policy.callbacks.callback.params.headers.label'),
          headers,
        ));
      }
    }
  }

  if (callback.enable !== undefined && callback.enable !== null) {
    rows.push((
      <ViewRowCheckbox
        checked={isEnabled(callback.enable)}
        key="enable"
        label={tView('callbacks.enable')}
      />
    ));
  }

  return (
    <ViewCard
      header={(
        <ViewRowTitle label={t('handlers:policies.storage_policy.callbacks.callback.type')}>
          {typeValue}
        </ViewRowTitle>
      )}
      value={`callback-${index}`}
    >
      {rows}
      <FilterRows filters={callback.filters} />
    </ViewCard>
  );
}

function CallbacksSection({ handler }: CallbacksSectionProps): React.JSX.Element | null {
  const callbacks = (handler.policies.storage_policy?.callbacks ?? [])
    .filter((callback): callback is HandlerHttpCallback | HandlerKafkaCallback => !isLunaWsCallback(callback));

  if (callbacks.length === 0) return null;

  return (
    <React.Fragment>
      {callbacks.map((callback, index) => (
        <CallbackCard
          callback={callback}
          index={index}
          // eslint-disable-next-line react/no-array-index-key
          key={`${callback.type}-${index}`}
        />
      ))}
    </React.Fragment>
  );
}

export { CallbacksSection };
